import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedList, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Gain } from '@sorare/core/src/components/scoring/Gain';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { ProjectionScore } from '@sorare/core/src/components/scoring/ProjectionScore';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';
import {
  anyPositionShortNames,
  positionNames,
} from '@sorare/core/src/lib/players';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { AnyPlayerStatsIcon } from 'components/player/AnyPlayerStatsIcon';
import { Grade } from 'components/player/Grade';

import type { Prediction_anyPlayer } from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ as: LinkBox })`
  background-color: var(--c-nd-50);
  border-radius: var(--unit);
  padding: 0 var(--intermediate-unit);
  align-items: stretch;
  min-width: 320px;
  min-height: 66px;
`;

const Metas = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
  padding: var(--intermediate-unit) 0;
  justify-content: center;
`;

const ImgWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: 60px;
  flex-shrink: 0;
  flex-grow: 0;
  overflow: hidden;
`;
const StyledResponsiveImg = styled(ResponsiveImg)`
  position: absolute;
  left: 0;
  top: var(--unit);
  width: 60px;
`;

const Scores = styled(Horizontal)`
  flex-shrink: 0;
  flex-grow: 0;
`;

const StyledBrandS = styled(BrandS)`
  white-space: nowrap;
`;

const POSITION_NAMES = {
  [Sport.FOOTBALL]: positionNames,
  [Sport.BASEBALL]: anyPositionShortNames,
  [Sport.NBA]: positionNames,
};

type Props = {
  anyPlayer: Prediction_anyPlayer;
  onStatsClick: (slug: string) => void;
  onMarketClick: (slug: string) => void;
  track?: (p: {
    target: ClickMarketHomeSection_Target;
    playerSlug?: string;
  }) => void;
};

export const Prediction = ({
  anyPlayer,
  onStatsClick,
  onMarketClick,
  track,
}: Props) => {
  const { sport } = anyPlayer;
  const positionLabels = POSITION_NAMES[sport];
  const {
    flags: { useHandleL10Gain = false },
  } = useFeatureFlags();
  return (
    <Root gap={2}>
      <ImgWrapper>
        <StyledResponsiveImg
          src={anyPlayer.fullPictureUrl}
          cropWidth={80}
          alt=""
        />
      </ImgWrapper>
      <Metas>
        <StyledBrandS>
          <LinkOverlay
            as="button"
            onClick={() => {
              onMarketClick(anyPlayer.slug);
              track?.({
                target: ClickMarketHomeSection_Target.PLAYER_MARKET_DRAWER,
                playerSlug: anyPlayer.slug,
              });
            }}
          >
            {anyPlayer.displayName}
          </LinkOverlay>
        </StyledBrandS>
        <LabelS color="var(--c-nd-600)">
          <FormattedList
            type="conjunction"
            value={anyPlayer.anyPositions.map(position => (
              <FormattedMessage {...positionLabels[position]} key={position} />
            ))}
          />
        </LabelS>
      </Metas>
      <Scores>
        <LinkOther as="div">
          <AnyPlayerStatsIcon
            player={anyPlayer}
            onClick={() => {
              onStatsClick(anyPlayer.slug);
              track?.({
                target: ClickMarketHomeSection_Target.PLAYER_DETAILS,
                playerSlug: anyPlayer.slug,
              });
            }}
          />
        </LinkOther>
        {isType(anyPlayer, 'Player') && (
          <Grade
            size={3}
            projection={anyPlayer.nextClassicFixtureProjectedGrade}
          />
        )}
        {isType(anyPlayer, 'BaseballPlayer') && (
          <ProjectionScore
            withTooltip
            projectedScore={anyPlayer.nextClassicFixtureProjectedScore}
            so5FixtureEvent={So5FixtureEvent.CLASSIC}
            size={3}
          />
        )}
        {isType(anyPlayer, 'NBAPlayer') && (
          <>
            <PlayerScore
              mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
              small
              player={anyPlayer}
            />

            <ProjectionScore
              withTooltip
              withLogo
              projectedScore={anyPlayer.nextClassicFixtureProjectedScore}
              so5FixtureEvent={So5FixtureEvent.CLASSIC}
              size={3}
            />
            {!!useHandleL10Gain && !!anyPlayer.gainOnClassicProjection && (
              <Gain
                size={3}
                value={anyPlayer.gainOnClassicProjection}
                strokeColor="var(--c-nd-50-opaque)"
              />
            )}
          </>
        )}
      </Scores>
    </Root>
  );
};

Prediction.fragments = {
  anyPlayer: gql`
    fragment Prediction_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      fullPictureUrl
      anyPositions
      nextClassicFixtureProjectedScore
      gainOnClassicProjection
      nextClassicFixtureProjectedGrade {
        id
        ...Grade_playerGameScoreProjection
      }
      ...AnyPlayerStatsIcon_anyPlayer
      ...PlayerScore_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${Grade.fragments.projection}
  ` as TypedDocumentNode<Prediction_anyPlayer>,
};
